import { pgEnum } from 'drizzle-orm/pg-core';

// Access & Entitlements
export const accessSourceType = pgEnum('AccessSourceType', ['SUBSCRIPTION', 'MANUAL', 'PROMOTION', 'GIFT', 'ORDER']);
export const accessStatus = pgEnum('AccessStatus', ['ACTIVE', 'EXPIRED', 'REVOKED']);
export const accessTargetType = pgEnum('AccessTargetType', ['COURSE', 'MODULE', 'MODULE_ITEM', 'CHANNEL', 'CONTENT', 'BOOK', 'LIVESTREAM']);

// Tag Assignment — единый enum для всех типов сущностей, на которые можно повесить тег.
// USER — атрибут профиля (раньше UserTag). Контент-сущности — видимость (раньше ContentTag).
// Контент с пустым набором тегов видят все; с тегами — только юзеры с пересечением их USER-тегов.
export const tagTargetType = pgEnum('TagTargetType', [
  'USER',
  'BOOK',
  'COURSE',
  'MODULE',
  'MODULE_ITEM',
  'LIVESTREAM',
  'TELEGRAM_CHANNEL',
  'CHANNEL_PASS',
  'SUBSCRIPTION_PLAN',
  'PRICING_TIER',
  'BANNER',
  'MINI_BANNER',
  'PROMO_ANNOUNCEMENT',
]);

// Promo announcement — тип события аналитики (fullscreen-промо при входе на платформу).
export const promoAnnouncementEventType = pgEnum('PromoAnnouncementEventType', ['IMPRESSION', 'CLICK', 'CLOSE']);

// Broadcasts
export const broadcastStatus = pgEnum('BroadcastStatus', ['DRAFT', 'SCHEDULED', 'SENDING', 'COMPLETED', 'CANCELLED', 'FAILED']);

// Email delivery — источник письма, к которому привязан трекинг-пиксель.
// INVITATION — приглашение на платформу (UserInvitation).
// BROADCAST — массовая рассылка (BroadcastDelivery).
// TRIGGER — автоматическое триггерное письмо (TriggerLog).
// ORDER_INVOICE — инвойс заказа.
// MAGIC_LINK — magic-link авторизации платформы.
// ADMIN_INVITE — приглашение администратора.
// ADMIN_RESET — сброс пароля администратора.
// OTP_PIN — одноразовый PIN-код пользователя.
// INTEGRATION — служебные письма интеграций (api-keys и т.п.).
// OTHER — прочее, без специфического sourceId.
export const emailDeliverySource = pgEnum('EmailDeliverySource', [
  'INVITATION',
  'BROADCAST',
  'TRIGGER',
  'ORDER_INVOICE',
  'MAGIC_LINK',
  'ADMIN_INVITE',
  'ADMIN_RESET',
  'OTP_PIN',
  'INTEGRATION',
  'OTHER',
]);

// Knowledge Base
export const documentStatus = pgEnum('DocumentStatus', ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED']);

// Files
export const fileAccess = pgEnum('FileAccess', ['PUBLIC', 'PRIVATE']);
export const fileStatus = pgEnum('FileStatus', ['UPLOADED', 'PROCESSING', 'READY', 'ERROR']);
// storageProvider — where the underlying bytes actually live.
//   LOCAL — local filesystem (UPLOAD_DIR or STORAGE_DIR on the backend host)
//   B2    — Backblaze B2 object storage (S3-compatible)
// The `bucket` text column distinguishes which bucket within a provider
// (e.g. 'local'/'storage' for LOCAL, 'mastersclub-public'/'mc-private-bucket' for B2).
export const storageProvider = pgEnum('StorageProvider', ['LOCAL', 'B2']);

// Courses & Homework
export const homeworkStatus = pgEnum('HomeworkStatus', ['NOT_STARTED', 'SUBMITTED', 'REVIEWED']);
export const homeworkType = pgEnum('HomeworkType', ['ONE_TIME', 'DAILY_STREAK', 'PERIOD']);
export const moduleItemMediaType = pgEnum('ModuleItemMediaType', ['SUBTITLE', 'AUDIO', 'VIDEO']);
export const moduleItemType = pgEnum('ModuleItemType', ['VIDEO', 'ARTICLE', 'HOMEWORK', 'BOOK', 'AUDIO']);
export const courseType = pgEnum('CourseType', ['ONLINE', 'MEETING', 'OFFLINE', 'WEBINAR']);
export const videoProcessingStatus = pgEnum('VideoProcessingStatus', ['PENDING', 'PROCESSING', 'READY', 'ERROR']);

// Library — Books & Audiobooks
export const bookFormat = pgEnum('BookFormat', ['EPUB', 'PDF', 'FB2', 'AUDIO']);
export const bookProcessingStatus = pgEnum('BookProcessingStatus', ['PENDING', 'PROCESSING', 'READY', 'ERROR']);

// Channel Memberships
export const membershipStatus = pgEnum('MembershipStatus', ['PENDING', 'JOIN_REQUEST_SENT', 'ACTIVE', 'LEFT', 'KICKED', 'BOT_BLOCKED', 'EXPIRED']);

// User ↔ Telegram Bot link — пользуется ботом / заблокировал бота
export const userBotLinkStatus = pgEnum('UserBotLinkStatus', ['ACTIVE', 'BLOCKED']);

// Telegram Chat Types
export const telegramChatType = pgEnum('TelegramChatType', ['channel', 'group', 'supergroup']);

// Email Templates — ключ шаблона письма (по нему + языку лежит TipTap-документ
// и готовый Handlebars-HTML в таблице EmailTemplate). Значения соответствуют
// шаблонам в apps/backend/src/shared/email-templates/*.tsx.
export const emailTemplateKey = pgEnum('EmailTemplateKey', [
  'OTP',
  'ADMIN_INVITE',
  'ADMIN_RESET',
  'NOTIFICATION',
]);

// Message Templates — единая таблица шаблонов (письма + Telegram-приглашения
// + платёжная ссылка заказа). Заменяет EmailTemplate + InvitationTemplate.
export const messageTemplateType = pgEnum('MessageTemplateType', [
  'OTP',
  'ADMIN_INVITE',
  'ADMIN_RESET',
  'NOTIFICATION',
  'ORDER_INVOICE',
  'INVITATION_EMAIL',
  'INVITATION_TELEGRAM',
]);

export const messageTemplateChannel = pgEnum('MessageTemplateChannel', [
  'EMAIL',
  'TELEGRAM',
  'TELEGRAM_SESSION',
]);

// Notifications
export const notificationChannel = pgEnum('NotificationChannel', ['APP', 'EMAIL', 'TELEGRAM']);
export const notificationType = pgEnum('NotificationType', [
  // User-facing
  'SUBSCRIPTION_EXPIRING', 'SUBSCRIPTION_EXPIRED', 'COURSE_AVAILABLE', 'HOMEWORK_REMINDER',
  'BROADCAST', 'SYSTEM', 'MARKETING',
  'FAMILY_SLOTS_INCREASED', 'FAMILY_SLOTS_DECREASED', 'FAMILY_MEMBER_ADDED', 'FAMILY_MEMBER_REMOVED',
  // Admin-facing — типизированная таксономия событий для admin-колокольчика и toast.
  // Дискриминируем семейства событий: PAYMENT_* (платежи/возвраты/фискалка),
  // ORDER_* (заказы), SUPPORT_* (тикеты), LIVESTREAM_* (трансляции),
  // TELEGRAM_* (userbot-сессии/floodwait), USER_* (подозрительные регистрации),
  // ENTITY_* (CRUD по сущностям через middleware), ERROR_CRITICAL / RECURRENT_FAILED /
  // PROMO_* / DISCOUNT_*. Используется как ключ для иконки/цвета/toast-уровня на фронте.
  'ADMIN_PAYMENT_SUCCESS', 'ADMIN_PAYMENT_FAILED', 'ADMIN_PAYMENT_REFUND',
  'ADMIN_FISCAL_ERROR',
  'ADMIN_ORDER_NEW', 'ADMIN_ORDER_CANCELLED',
  'ADMIN_SUPPORT_TICKET_NEW', 'ADMIN_SUPPORT_TICKET_REPLY',
  'ADMIN_LIVESTREAM_STARTED', 'ADMIN_LIVESTREAM_STREAM_LOST', 'ADMIN_LIVESTREAM_OBS_ERROR',
  'ADMIN_TELEGRAM_SESSION_EXPIRED', 'ADMIN_TELEGRAM_FLOOD_WAIT',
  'ADMIN_USER_DUPLICATE',
  'ADMIN_ENTITY_CREATED', 'ADMIN_ENTITY_UPDATED', 'ADMIN_ENTITY_DELETED',
  'ADMIN_ERROR_CRITICAL',
  'ADMIN_PROMO_LIMIT', 'ADMIN_PROMO_EXPIRED',
  'ADMIN_DISCOUNT_BUDGET_EXCEEDED',
  'ADMIN_RECURRENT_FAILED',
]);

// Commerce & Orders
export const orderTargetType = pgEnum('OrderTargetType', ['COURSE', 'CHANNEL_PASS', 'SUBSCRIPTION_PLAN', 'BONUS', 'BOOK']);
// Reviews (polymorphic)
export const reviewTargetType = pgEnum('ReviewTargetType', ['COURSE', 'CHANNEL_PASS', 'SUBSCRIPTION_PLAN']);

// Payments
export const paymentProvider = pgEnum('PaymentProvider', ['MANUAL', 'CLICK', 'PAYME', 'STRIPE', 'MULTICARD', 'CLOUDPAYMENTS']);
export const paymentStatus = pgEnum('PaymentStatus', ['PENDING', 'CONFIRMED', 'REJECTED', 'REFUNDED', 'PARTIALLY_REFUNDED']);

// Presence (online tracking source)
export const presenceSource = pgEnum('PresenceSource', ['WEB', 'BOT', 'MINI_APP']);

// Daily Planner
export const repeatType = pgEnum('RepeatType', ['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM']);

// Subscriptions
export const subscriptionStatus = pgEnum('SubscriptionStatus', ['PENDING', 'ACTIVE', 'EXPIRED', 'CANCELLED']);
export const familyMemberStatus = pgEnum('FamilyMemberStatus', ['ACTIVE', 'REMOVED', 'PENDING']);

// Translations
export const translatableEntityType = pgEnum('TranslatableEntityType', [
  'COURSE', 'MODULE', 'MODULE_ITEM', 'ARTICLE', 'ANNOUNCEMENT', 'BANNER', 'CATEGORY', 'TAG',
  'TEACHER', 'GOAL', 'REMINDER', 'NOTIFICATION_TEMPLATE',
  'CHANNEL_PASS', 'SUBSCRIPTION_PLAN', 'BROADCAST',
  'PRICING_TIER', 'BONUS_TEMPLATE', 'PROMOTION', 'CLUB_SETTINGS',
  'LEGAL_PAGE', 'MINI_BANNER', 'LIVESTREAM',
]);

// Mini-banners — target scope (courses, modules, lessons, livestreams)
export const miniBannerTargetType = pgEnum('MiniBannerTargetType', ['COURSE', 'MODULE', 'MODULE_ITEM', 'LIVESTREAM']);

// Triggers
export const triggerEvent = pgEnum('TriggerEvent', [
  'SUBSCRIPTION_PURCHASED', 'SUBSCRIPTION_STARTED', 'SUBSCRIPTION_EXPIRING', 'SUBSCRIPTION_EXPIRED',
  'SUBSCRIPTION_EXPIRATION', 'ORDER_CREATED', 'ORDER_PAID', 'ORDER_CANCELLED',
  'COURSE_COMPLETED', 'HOMEWORK_SUBMITTED', 'USER_REGISTERED', 'NEW_COURSE_AVAILABLE',
  'CHANNEL_PASS_LIMIT_REACHED', 'CHANNEL_PASS_EXPIRED',
  'COURSE_ACCESS_EXPIRED', 'COURSE_DRIP_RELEASED',
  'FAMILY_MEMBER_ADDED', 'FAMILY_MEMBER_REMOVED', 'FAMILY_SLOTS_CHANGED',
  // Livestream events — см. apps/backend/src/domains/livestreams.
  'LIVESTREAM_STARTED', 'LIVESTREAM_SCHEDULED_REMINDER',
  'LIVESTREAM_CANCELLED', 'LIVESTREAM_ENDING_SOON',
  // Low-level access grant (per-grant) — см. access.service.ts grantAccess().
  'ACCESS_GRANTED',
  // Ответ на коммент к уроку — emit из lesson-comments.service.ts.reply().
  'COMMENT_REPLY',
]);

// Channel Pass limit actions
export const limitAction = pgEnum('LimitAction', ['HIDE_FROM_STORE', 'DEACTIVATE', 'TRIGGER']);

// Admin AI Chat
export const adminAiMessageRole = pgEnum('AdminAiMessageRole', ['user', 'assistant', 'tool']);

// AI Chat — исход обработки запроса пользовательским агентом (для аналитики)
export const aiChatStatus = pgEnum('AiChatStatus', [
  'success',
  'fallback_iterations',
  'error',
  'rate_limited',
  'timeout',
]);

// Social Features - Content Status
export const contentStatus = pgEnum('ContentStatus', ['DRAFT', 'PENDING_MODERATION', 'PUBLISHED', 'REJECTED']);

// Social Features - Privacy Level
export const privacyLevel = pgEnum('PrivacyLevel', ['PUBLIC', 'FOLLOWERS', 'PRIVATE']);

// Social Features - Moderatable Entity Type
export const moderatableType = pgEnum('ModeratableType', ['POST', 'COMMENT', 'REVIEW']);

// User Invitations
export const invitationStatus = pgEnum('InvitationStatus', ['PENDING', 'SENT', 'ACCEPTED', 'FAILED', 'EXPIRED']);

// Канал доставки приглашения: Telegram (ЛС через GramJS) или Email (письмо со ссылкой на платформу)
export const invitationChannel = pgEnum('InvitationChannel', ['TELEGRAM', 'EMAIL']);

// User deletion reason — причина почему аккаунт юзера был помечен удалённым.
// Используется для решения auth-handler'а: блокировать повторный вход или нет,
// а также для аудита в админке.
export const userDeletionReason = pgEnum('UserDeletionReason', [
  'ADMIN_DELETED', // админ удалил через панель; повторный вход заблокирован
  'MERGED', // аккаунт смерджен в другой (см. User.mergedIntoUserId и UserMergeHistory)
]);

// User block reason — причина админской блокировки пользователя.
// В отличие от userDeletionReason (необратимое удаление), блокировка обратима
// через unblockUser. Используется для audit и отображения юзеру причины блока.
export const userBlockReason = pgEnum('UserBlockReason', [
  'SPAM',
  'ABUSE',
  'FRAUD',
  'PAYMENT_CHARGEBACK',
  'TOS_VIOLATION',
  'OTHER',
]);

// Тип действия в истории блокировок (UserBlockAudit):
// BLOCK — админ заблокировал, UNBLOCK — админ снял блокировку.
export const userBlockAuditAction = pgEnum('UserBlockAuditAction', [
  'BLOCK',
  'UNBLOCK',
]);

// OAuth-провайдеры для внешних способов входа (Google, Apple и т.п.).
// Используется таблицей OAuthAccount для связи User ↔ внешний identity.
export const oauthProvider = pgEnum('OAuthProvider', [
  'GOOGLE',
  'APPLE',
]);

// Тип identity для блокировки повторного входа удалённых пользователей.
// Используется таблицей BlockedIdentity для lock-key независимо от метода логина.
export const blockedIdentityType = pgEnum('BlockedIdentityType', [
  'EMAIL',
  'TELEGRAM_ID',
  'PHONE',
  'GOOGLE_SUB',
  'APPLE_SUB',
]);

// Причина деактивации поста. Отличается от deletionReason (soft-delete) —
// деактивированный пост не удалён, он виден админу и автору, но скрыт от
// пользователей и не может быть активирован обратно.
// AUTHOR_DELETED — автор был удалён админом, пост автоматически деактивирован.
export const postDeactivationReason = pgEnum('PostDeactivationReason', [
  'AUTHOR_DELETED',
]);

// ==========================================
// CHAT / MESSAGING
// ==========================================

// Тип чата
export const chatRoomType = pgEnum('ChatRoomType', ['DIRECT', 'SUPPORT', 'GAME_LOBBY', 'GROUP']);

// Роль участника чата
export const chatParticipantRole = pgEnum('ChatParticipantRole', ['OWNER', 'ADMIN', 'MEMBER']);

// Статус доставки сообщения
export const messageDeliveryStatus = pgEnum('MessageDeliveryStatus', ['SENT', 'DELIVERED', 'READ']);

// Статус участника в чате
export const chatParticipantStatus = pgEnum('ChatParticipantStatus', ['ACTIVE', 'LEFT', 'KICKED', 'BLOCKED']);

// ==========================================
// GAMES
// ==========================================

// Тип игры
export const gameType = pgEnum('GameType', ['CARD_COLOR', 'GUESS_NUMBER']);

// Статус лобби
export const lobbyStatus = pgEnum('LobbyStatus', ['WAITING', 'STARTING', 'IN_PROGRESS', 'FINISHED', 'CANCELLED']);

// Статус игрока в лобби
export const lobbyPlayerStatus = pgEnum('LobbyPlayerStatus', ['WAITING', 'READY', 'PLAYING', 'LEFT', 'KICKED']);

// ==========================================
// E2EE — End-to-end encryption
// ==========================================

// Платформа устройства (привязка к Olm device).
// PLATFORM — apps/platform (web/Next.js), MINI_APP — Telegram Mini App,
// BOT — bot-side device (для service-комнат бот не использует E2EE, но
// если решим шифровать notifications — тут будет device), DESKTOP — на
// будущее (отдельные desktop-клиенты).
export const devicePlatform = pgEnum('DevicePlatform', ['PLATFORM', 'MINI_APP', 'BOT', 'DESKTOP']);

// Статус устройства. ACTIVE — может слать/принимать. REVOKED — юзер
// логически удалил девайс; ключи не выдаются, history-blob не пишется.
export const deviceStatus = pgEnum('DeviceStatus', ['ACTIVE', 'REVOKED']);

// Статус key-backup-а. ACTIVE — текущая используемая версия бэкапа.
// TRASHED — старая версия, оставлена для возможного recovery.
export const keyBackupStatus = pgEnum('KeyBackupStatus', ['ACTIVE', 'TRASHED']);

// Канал доставки reset-кода при сбросе PIN. EMAIL — на подтверждённый email,
// TELEGRAM — сообщением от бота в привязанный Telegram аккаунт.
export const resetCodeChannel = pgEnum('ResetCodeChannel', ['EMAIL', 'TELEGRAM']);

// Тип чата по политике приватности.
// USER_E2EE — сквозное шифрование между юзерами (Olm/Megolm),
// SERVICE — служебные сообщения от бота (без E2EE),
// ADMIN — переписка с админом/поддержкой (без E2EE — админ читает plaintext),
// DIRECT — обычный 1:1 чат без шифрования (legacy/non-E2EE),
// GROUP — групповой чат без шифрования.
// Поле `kind` на ChatRoom — это политика-определяющее поле, отдельное от
// исторического `type`. `isE2EE` immutable: сервис-слой не должен позволять
// его менять после create.
export const chatRoomKind = pgEnum('ChatRoomKind', [
  'USER_E2EE',
  'SERVICE',
  'ADMIN',
  'DIRECT',
  'GROUP',
  'LIVESTREAM',
]);

// ==========================================
// LIVESTREAM — OBS→RTMP→WebRTC/HLS прямые трансляции
// ==========================================

// Жизненный цикл трансляции.
// SCHEDULED — создана, ожидает старта. LIVE — идёт эфир (RTMP connected).
// RECONNECTING — RTMP разрыв, ждём восстановления публикации.
// ENDED — эфир завершён штатно. CANCELLED — отменён админом до старта.
export const livestreamStatus = pgEnum('LivestreamStatus', [
  'SCHEDULED',
  'LIVE',
  'RECONNECTING',
  'ENDED',
  'CANCELLED',
]);

// Видимость трансляции. PUBLIC — доступна всем, PRIVATE — только audience.
export const livestreamVisibility = pgEnum('LivestreamVisibility', ['PUBLIC', 'PRIVATE']);

// Источник входящего RTMP-потока, который отдаётся зрителям.
// OBS  — ведущий пушит из OBS Studio на path `live/<streamKey>`.
// ZOOM — Zoom Custom Live Stream пушит на отдельный path `live/<streamKey>-zoom`.
// Оба источника могут публиковаться одновременно (раздельные path в MediaMTX);
// `Livestream.activeSource` определяет, какой из них видят зрители (hot-swap).
export const livestreamSource = pgEnum('LivestreamSource', ['OBS', 'ZOOM']);

// MCP-сервер: вид токена доступа ИИ-клиента (docs/mcp-server-plan.md §6)
export const mcpTokenKind = pgEnum('McpTokenKind', ['PERSONAL', 'OAUTH_ACCESS', 'OAUTH_REFRESH']);

// ============================================
// Real-time перевод эфиров (STT → MT → TTS)
// Канон: docs/livestream-translation-architecture.md §5.2, §7
// ============================================

// Политика нарезки речи на сегменты. Дубляж не может начать озвучивать
// раньше конца committed-фразы — это structural floor задержки (2.5–4 с).
// BALANCED — лекции; LOW_LATENCY — Q&A (короче фразы ценой смысла);
// HIGH_QUALITY — доклады (ждём clause/sentence).
export const translationSegmentPolicy = pgEnum('TranslationSegmentPolicy', [
  'BALANCED',
  'LOW_LATENCY',
  'HIGH_QUALITY',
]);

// Как выбирается голос озвучки.
// SINGLE — один голос на эфир (дефолт v1). PER_SPEAKER — diarization label →
// slot → свой clone (включается только после проверки realtime-диаризации).
// NEUTRAL — нейтральный пресет. Незнакомый спикер всегда → NEUTRAL,
// никогда чужой именной clone.
export const translationVoiceMode = pgEnum('TranslationVoiceMode', [
  'SINGLE',
  'PER_SPEAKER',
  'NEUTRAL',
]);

// Режим восприятия зрителя. MIX — обе дорожки одновременно с независимыми
// громкостями («слышать и зал, и перевод»).
export const translationViewerMode = pgEnum('TranslationViewerMode', [
  'ORIGINAL',
  'CAPTIONS',
  'DUB',
  'DUB_PLUS_CAPTIONS',
  'MIX',
]);

// Судьба оригинальной дорожки под озвучкой.
export const translationOriginalMix = pgEnum('TranslationOriginalMix', [
  'MUTE',
  'MIX',
  'DUCK_DB',
]);

// Когда стартует сессия перевода: автоматически при выходе эфира в LIVE
// или вручную кнопкой админа.
export const translationStartPolicy = pgEnum('TranslationStartPolicy', ['ON_LIVE', 'MANUAL']);

// Жизненный цикл сессии перевода — независим от статуса самого эфира
// (падение перевода не валит оригинальный playback).
export const translationSessionStatus = pgEnum('TranslationSessionStatus', [
  'STARTING',
  'LIVE',
  'DEGRADED',
  'STOPPING',
  'STOPPED',
  'FAILED',
]);

// Происхождение голоса в библиотеке тенанта.
export const translationVoiceProfileKind = pgEnum('TranslationVoiceProfileKind', [
  'CLONE',
  'PRESET',
  'NEUTRAL',
]);

// Провайдер синтеза речи для профиля голоса.
export const translationVoiceProvider = pgEnum('TranslationVoiceProvider', ['ELEVENLABS']);

// Статус игровой сессии
export const gameSessionStatus = pgEnum('GameSessionStatus', ['ACTIVE', 'FINISHED', 'TIMEOUT', 'CANCELLED']);

// Категория жалобы на игрока
export const playerReportCategory = pgEnum('PlayerReportCategory', ['DISRUPTING', 'OFFENSIVE', 'CHEATING', 'OTHER']);

// Статус жалобы
export const playerReportStatus = pgEnum('PlayerReportStatus', ['PENDING', 'REVIEWED', 'RESOLVED', 'DISMISSED']);

// Категория достижения
export const achievementCategory = pgEnum('AchievementCategory', ['CARD_COLOR', 'GUESS_NUMBER', 'SOCIAL', 'GENERAL']);

// Тип условия достижения
export const achievementConditionType = pgEnum('AchievementConditionType', ['COUNT', 'STREAK', 'FIRST', 'CUSTOM']);

// ==========================================
// PRICING TIERS & USER BONUSES
// ==========================================

// Тип бонуса
export const bonusType = pgEnum('BonusType', ['NUMBER', 'BOOLEAN', 'ARRAY', 'STRING']);

// Режим длительности бонуса
export const bonusDurationMode = pgEnum('BonusDurationMode', ['RELATIVE', 'ABSOLUTE', 'INHERIT']);

// Статус бонуса пользователя
export const userBonusStatus = pgEnum('UserBonusStatus', ['ACTIVE', 'EXPIRED', 'REVOKED']);

// Тип действия над бонусом
export const userBonusActionType = pgEnum('UserBonusActionType', [
  'INCREMENT', 'DECREMENT', 'SET', 'PUSH', 'REMOVE', 'REVOKE', 'EXPIRE',
]);

// ==========================================
// LEADS CRM
// ==========================================

// Источник присвоения тега лиду.
// manual — назначен вручную администратором.
// utm_auto — назначен автоматически на основе UTM-параметров.
// webhook — назначен автоматически при создании лида через webhook.
export const leadTagSource = pgEnum('LeadTagSource', ['manual', 'utm_auto', 'webhook']);

// ==========================================
// VIDEO CALLS
// ==========================================

// Тип видеозвонка. CALL — обычный звонок, STAGE — режим сцены (вебинар).
export const callRoomType = pgEnum('CallRoomType', ['CALL', 'STAGE']);

// Жизненный цикл комнаты звонка.
// SCHEDULED — запланирован, ожидает старта.
// ACTIVE — идёт звонок (участники подключены).
// ENDED — завершён.
export const callRoomStatus = pgEnum('CallRoomStatus', ['SCHEDULED', 'ACTIVE', 'ENDED']);

// Роль участника в звонке.
// HOST — организатор, SPEAKER — выступающий (на сцене), VIEWER — зритель.
export const callParticipantRole = pgEnum('CallParticipantRole', ['HOST', 'SPEAKER', 'VIEWER']);

// Статус приглашения в звонок.
// PENDING — ожидает ответа, ACCEPTED — принято, DECLINED — отклонено, EXPIRED — истекло.
export const callInvitationStatus = pgEnum('CallInvitationStatus', ['PENDING', 'ACCEPTED', 'DECLINED', 'EXPIRED']);

// ==========================================
// BREAKOUT ROOMS (сессионные залы трансляции)
// ==========================================

// Жизненный цикл сессии сессионных залов.
// OPEN — залы открыты, участники распределены; CLOSED — залы закрыты, участники возвращены на трансляцию.
export const breakoutSessionStatus = pgEnum('BreakoutSessionStatus', ['OPEN', 'CLOSED']);

// Режим распределения участников по залам.
// AUTO — система раскидывает равномерно; MANUAL — админ задаёт назначения вручную.
export const breakoutSessionMode = pgEnum('BreakoutSessionMode', ['AUTO', 'MANUAL']);

// ==========================================
// PROMO CODES
// ==========================================

// Тип скидки промокода:
// PERCENT — процент от суммы заказа, FIXED — фиксированная сумма,
// PRICE_OVERRIDE — замена цены на указанное значение.
export const promoDiscountType = pgEnum('PromoDiscountType', ['PERCENT', 'FIXED', 'PRICE_OVERRIDE']);

// Целевой тип применения промокода/автоскидки:
// ALL — любой заказ, COURSE — конкретный курс, CHANNEL_PASS — подписка в канал,
// SUBSCRIPTION_PLAN — абонемент целиком, PRICING_TIER — конкретный тариф (любого товара), BOOK — книга.
export const promoTargetType = pgEnum('PromoTargetType', ['ALL', 'COURSE', 'CHANNEL_PASS', 'SUBSCRIPTION_PLAN', 'BOOK', 'PRICING_TIER']);
